import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { MULTI_TECHNICIAN_MAX } from "../lib/assist";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const MODES = [
  { value: "single", label: "Un seul technicien", hint: "Seul le technicien qui prend la demande en charge rejoint l'assistance." },
  { value: "multi", label: "Plusieurs techniciens",
    hint: "Un collègue peut rejoindre une assistance en cours : il voit l'écran partagé, entend et peut activer son micro." },
];

// Paramètres › Assistance — multi-technician mode (settings:view to read, settings:manage to change; the server checks it).
export default function SettingsAssist() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [mode, setMode] = useState("single");
  const [max, setMax] = useState("2");
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = (settings) => {
    setSaved(settings);
    setMode(settings["multi_technician.enabled"] ? "multi" : "single");
    setMax(String(settings["multi_technician.max"]));
  };

  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/assist");
      apply(data.settings);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = saved != null
    && ((mode === "multi") !== !!saved["multi_technician.enabled"] || max !== String(saved["multi_technician.max"]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/assist", {
        "multi_technician.enabled": mode === "multi",
        "multi_technician.max": Number(max),
      });
      apply(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-assist">
      <SectionHeader title="Assistance" description="Qui peut se joindre à une assistance à distance en cours." />

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
        <div>
          <div className="text-sm font-medium text-slate-900">Techniciens par assistance</div>
          <RadioGroup value={mode} onValueChange={setMode} disabled={!canManage} className="mt-3 space-y-2" aria-label="Techniciens par assistance">
            {MODES.map((m) => (
              <label key={m.value} htmlFor={`assist-mode-${m.value}`}
                className={`flex items-start gap-3 rounded-lg border p-3 ${mode === m.value ? "border-jamora/40 bg-jamora/5" : "border-slate-200"} ${canManage ? "cursor-pointer" : ""}`}>
                <RadioGroupItem value={m.value} id={`assist-mode-${m.value}`} className="mt-0.5" data-testid={`assist-mode-${m.value}`} />
                <span>
                  <span className="block text-sm font-medium text-slate-900">{m.label}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">{m.hint}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>

        {mode === "multi" && (
          <div className="border-t border-slate-100 pt-4 space-y-4">
            <div>
              <Label htmlFor="assist-max" className="text-sm">Nombre maximum de techniciens</Label>
              <p className="text-xs text-slate-500 mt-0.5">Par assistance, technicien principal compris.</p>
              <Select value={max} onValueChange={setMax} disabled={!canManage}>
                <SelectTrigger id="assist-max" className="mt-1.5 w-[180px]" data-testid="assist-max"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: MULTI_TECHNICIAN_MAX - 1 }, (_, i) => i + 2).map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} techniciens</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
              <li>Tout technicien qui prend en charge des demandes peut rejoindre l'assistance d'un collègue depuis la file d'attente.</li>
              <li>Le pharmacien est informé de chaque arrivée et voit la liste des techniciens présents.</li>
              <li>Seul le technicien principal termine l'assistance et voit le code de vérification ; les autres peuvent la quitter.</li>
              <li>Nécessite une version de Jamora qui accepte plusieurs techniciens : sinon, aucune assistance n'est proposée à rejoindre.</li>
            </ul>
          </div>
        )}
      </div>

      <SaveBar dirty={dirty} saving={saving} canManage={canManage} onSave={save} onRevert={() => apply(saved)} />
    </div>
  );
}
